import { debounce } from "lodash";
import React, { useEffect, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useForm, useAddQuery, useScroll } from "../../../hooks";
import { getAdminEventTypesAction } from "../../../store/actions/adminEventType";
import { getAdminFilterListAction } from "../../../store/actions/adminFilterList";
import constant from "../../../util/constant";
import eventTypeConstant from "../../../util/eventTypeConstant";
import { concatStrings, useSetUrlParams, checkFilterApplied } from "../../../util/helpers";
import { EventTypes, SubHeader } from "../../shared/eventTypes";

const { ADMIN_TAB, EVENT_TYPE_API_TYPES } = eventTypeConstant;
const { ROUTES, ORDER_BY, API_STATUS, FILTERS_NAME } = constant;

const AdminEventTypeList = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { addQuery } = useAddQuery();
  const { scrollEvent } = useScroll();
  const isFilterApplied = checkFilterApplied( window.location.search, FILTERS_NAME.EVENT_TYPES );
  const paramsQuery = new URLSearchParams( window.location.search );
  const { eventTypes, eventTypesMetaData, status, apiType, eventNameList,
    eventTypeIdsList, eventUnitList, partnerList } = useSelector( state => ( {
    eventTypes: state.adminEventType.adminEventTypes.eventTypes,
    eventTypesMetaData: state.adminEventType.adminEventTypes.meta,
    status: state.adminEventType.status,
    apiType: state.adminEventType.apiType,
    eventNameList: state.adminFilterList.filterData.eventNameList,
    eventUnitList: state.adminFilterList.filterData.eventUnitList,
    eventTypeIdsList: state.adminFilterList.filterData.eventTypeIdList,
    partnerList: state.adminFilterList.filterData.partnerList,
  } ) );
  const [ showFilter, setShowFilter ] = useState( false );

  function getUrlParameter( key, col ) {
    let paramsQuerys = null;

    if( ( key === "searchKey" ) || ( key === "sortColumn" ) || ( key === "sortOrder" ) ) {
      paramsQuerys = paramsQuery.get( key ) === "" ? col : paramsQuery.get( key );
    }

    if( ( key === "eventNameList" ) || ( key === "unitList" ) || ( key === "partnerList" ) || ( key === "eventTypeIdsList" ) ) {
      paramsQuerys = paramsQuery.get( key ) === "" ? [] : JSON.parse( paramsQuery.get( key ) );
    }

    return paramsQuerys;
  }

  const { values, handleChange } = useForm(
    {
      page: 1,
      showPartnerEventTypes: false,
      isAdmin: true,
      activeTab: ADMIN_TAB.TAB1.tab,
      searchDummyText: getUrlParameter( "searchKey", "" ),
      searchKey: getUrlParameter( "searchKey", "" ),
      sortBy: getUrlParameter( "sortColumn", "" ),
      sortOrder: getUrlParameter( "sortOrder", ORDER_BY.ASC ),
      eventNameList: getUrlParameter( "eventNameList", "" ),
      unitList: getUrlParameter( "unitList", "" ),
      partnerList: getUrlParameter( "partnerList", "" ),
      eventTypeIdsList: getUrlParameter( "eventTypeIdsList", "" ),
    },
    {}
  );

  const handleTabClick = ( selectedTab, eventType ) => {
    handleChange( "showPartnerEventTypes", eventType );
    handleChange( "activeTab", selectedTab );
  };

  useEffect( () => {
    const requestParams = Object.assign(
      {
        page: values.page,
        showPartnerEventTypes: values.showPartnerEventTypes,
        sortOrder: values.sortOrder,
        perPage: 10,
        // TODO: refactor to remove use of undefined
        // May require a change to API
        // eslint-disable-next-line no-undefined
        eventNameList: concatStrings( values.eventNameList, "," ) || undefined,
        // TODO: refactor to remove use of undefined
        // May require a change to API
        // eslint-disable-next-line no-undefined
        unitList: concatStrings( values.unitList, "," ) || undefined,
        // TODO: refactor to remove use of undefined
        // May require a change to API
        // eslint-disable-next-line no-undefined
        partnerList: concatStrings( values.partnerList, "," ) || undefined,
        // TODO: refactor to remove use of undefined
        // May require a change to API
        // eslint-disable-next-line no-undefined
        eventTypeIdsList: concatStrings( values.eventTypeIdsList, "," ) || undefined,
      },
      values.searchKey && { searchKey: values.searchKey },
      values.sortBy && { sortBy: values.sortBy }
    );

    dispatch( getAdminEventTypesAction( requestParams ) );
  }, [
    values.sortBy,
    values.searchKey,
    values.showPartnerEventTypes,
    values.activeTab,
    values.sortOrder,
    values.page,
    values.eventNameList,
    values.unitList,
    values.partnerList,
    values.eventTypeIdsList,
  ] );

  useEffect( () => {
    if(
      status === API_STATUS.SUCCESS &&
      apiType === EVENT_TYPE_API_TYPES.GET_ADMIN_EVENT_TYPES
    ) {
      dispatch( getAdminFilterListAction( { entities: "EventType,Partner,EventUnit" } ) );
    }
  }, [ status, apiType ] );

  const handleSorting = ( sortBy, sortingType ) => {
    handleChange( "page", 1 );
    handleChange( "sortOrder", sortingType );
    handleChange( "sortBy", sortBy );
    addQuery( useSetUrlParams( "sortColumn", sortBy ) );
    addQuery( useSetUrlParams( "sortOrder", sortingType ) );
  };

  const onScrolls = ( e ) => {
    scrollEvent( e, eventTypes, values, handleChange, eventTypesMetaData, "page" );
  };

  const debouncedSave = useCallback(
    debounce( nextValue => handleChange( "searchKey", nextValue ), 1000 ),
    []
  );

  const searchHandle = ( e ) => {
    const { value: nextValue } = e.target;

    addQuery( useSetUrlParams( "searchKey", nextValue ) );
    handleChange( "searchDummyText", nextValue );
    debouncedSave( nextValue );
    handleChange( "page", 1 );
  };

  return (
    <>
      <div className='main-wrapper'>
        <div className='main-right-wrapper'>
          <SubHeader
            values={values}
            tabConfig={ADMIN_TAB}
            onClickTab={handleTabClick}
            onAddNew={() => history.push(
              ROUTES.ADMIN_ADD_EDIT_EVENT_TYPE.replace( ":eventTypeId", 0 )
            )}
            onSearch={searchHandle}
            handleChange={handleChange}
            eventNameList={eventNameList}
            eventUnitList={eventUnitList}
            partnerList={partnerList}
            eventTypeIdsList={eventTypeIdsList}
            addQuery={addQuery}
            isFilterApplied={isFilterApplied}
            updateShowFilterStatus={setShowFilter}
          />
          <EventTypes
            eventTypes={eventTypes}
            onSorting={handleSorting}
            onScroll={onScrolls}
            eventTypeEditUrl={ROUTES.ADMIN_ADD_EDIT_EVENT_TYPE}
            role='admin'
            showFilter={showFilter}
          />
        </div>
      </div>
    </>
  );
};

export default AdminEventTypeList;
